import { useState } from 'react';
import { Building2, Check, ChevronDown, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTenant } from '@/contexts/TenantContext';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import { CreateOrganizationDialog } from '@/components/tenant/CreateOrganizationDialog';

export function OrgSwitcher() {
  const { t } = useTranslation();
  const { currentOrg, organizations, switchOrg } = useTenant();
  const [createOpen, setCreateOpen] = useState(false);

  if (!currentOrg) return null;

  const showSwitcher = organizations.length > 1 || isExpaiApiEnabled();

  if (!showSwitcher) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 text-xs font-medium"
            aria-label={`Organización actual: ${currentOrg.name}. Cambiar organización`}
          >
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="hidden sm:inline max-w-[140px] truncate">{currentOrg.name}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {t('iam.org.switcherLabel', 'Organizations')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {organizations.map((org) => {
            const isCurrent = org.id === currentOrg.id;
            return (
              <DropdownMenuItem
                key={org.id}
                onClick={() => {
                  if (!isCurrent) void switchOrg(org.id);
                }}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm">{org.name}</span>
                  <span className="text-[10px] text-muted-foreground capitalize">
                    {org.role} · {org.plan}
                  </span>
                </div>
                {isCurrent && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
              </DropdownMenuItem>
            );
          })}
          {isExpaiApiEnabled() && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('iam.org.create', 'Create organization')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isExpaiApiEnabled() && (
        <CreateOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}
    </>
  );
}
